import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import API from "../../config/config";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const StepDrivingLicense = forwardRef(
  ({ formData, setFormData, driver, initialFormData = {}, disabled }, ref) => {
    const [loading, setLoading] = useState(false);
    const token = localStorage.getItem("adminToken");

    const [dlfrontPreview, setDlfrontPreview] = useState(null);
    const [dlbackPreview, setDlbackPreview] = useState(null);

    useEffect(() => {
      if (formData.dlfront && formData.dlfront instanceof File) {
        const url = URL.createObjectURL(formData.dlfront);
        setDlfrontPreview(url);
        return () => URL.revokeObjectURL(url);
      } else if (typeof formData.dlfront === "string") {
        setDlfrontPreview(formData.dlfront);
      } else {
        setDlfrontPreview(null);
      }
    }, [formData.dlfront]);

    useEffect(() => {
      if (formData.dlback && formData.dlback instanceof File) {
        const url = URL.createObjectURL(formData.dlback);
        setDlbackPreview(url);
        return () => URL.revokeObjectURL(url);
      } else if (typeof formData.dlback === "string") {
        setDlbackPreview(formData.dlback);
      } else {
        setDlbackPreview(null);
      }
    }, [formData.dlback]);

    const today = new Date();
    const minDOB = new Date(
      today.getFullYear() - 21,
      today.getMonth(),
      today.getDate()
    );
    const maxDOI = new Date(
      today.getFullYear() - 1,
      today.getMonth(),
      today.getDate()
    );
    const minDOE = new Date(
      today.getFullYear() + 1,
      today.getMonth(),
      today.getDate()
    );

    const handleChange = (e) => {
      const { name, value } = e.target;

      if (name === "nameToMatch") {
        const regex = /^[A-Za-z\s]*$/;
        if (!regex.test(value)) return;
      }

      if (name === "dlNumber") {
        const regex = /^[A-Za-z0-9]*$/; // only alphanumeric
        if (!regex.test(value)) return;
      }

      setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e, key) => {
      const file = e.target.files[0];
      if (file) {
        setFormData((prev) => ({ ...prev, [key]: file }));
      }
    };

    const uploadFile = async (file, type) => {
      const headers = new Headers();
      headers.append("Authorization", `Bearer ${token}`);

      const form = new FormData();
      form.append("documentFor", "Driver");
      form.append("type", type);
      form.append("ownerId", driver._id);
      form.append("file", file);

      const res = await fetch(API.ADMIN.ADMINDOCUMENT_UPLOAD, {
        method: "POST",
        headers,
        body: form,
      });

      if (!res.ok) throw new Error(`Upload failed for ${type}`);

      const data = await res.json();
      return data.document?.documentUrl;
    };

    const isFormDataChanged = () => {
      const keysToCheck = [
        "dlNumber",
        "nameToMatch",
        "dateOfBirth",
        "dateOfIssue",
        "dateOfExpire",
        "dlfront",
        "dlback",
      ];

      console.log("Initial:", initialFormData);
      console.log("Current:", formData);

      return keysToCheck.some((key) => {
        const initial = initialFormData[key];
        const current = formData[key];

        // Both empty — no change
        if (!initial && !current) return false;

        // If one is missing — changed
        if (!initial || !current) return true;

        // File-specific logic
        const isFileField = key === "dlfront" || key === "dlback";

        if (isFileField) {
          // Case 1: New file uploaded
          if (current instanceof File) {
            // Compare filename against initial URL string
            return (
              typeof initial !== "string" || !initial.includes(current.name)
            );
          }

          // Case 2: Both are strings (likely URLs)
          if (typeof current === "string" && typeof initial === "string") {
            // Normalize URLs by stripping query params or timestamp
            const normalize = (url) => url?.split("?")[0];
            return normalize(current) !== normalize(initial);
          }

          return true; // fallback
        }

        // Default field comparison
        return current !== initial;
      });
    };

    useImperativeHandle(ref, () => ({
      validateAndUpload: async () => {
        const requiredFields = [
          { field: "dlNumber", label: "Driving License Number" },
          { field: "nameToMatch", label: "Full Name" },
          { field: "dateOfBirth", label: "Date of Birth" },
          { field: "dateOfIssue", label: "Date of Issue" },
          { field: "dateOfExpire", label: "Date of Expiry" },
          { field: "dlfront", label: "DL Front Image" },
          { field: "dlback", label: "DL Back Image" },
        ];

        console.log(formData);

        for (const { field, label } of requiredFields) {
          if (!formData[field]) {
            toast.error(`Please provide ${label}.`);
            throw new Error(`Please provide ${label}.`);
          }
        }

        const changed = isFormDataChanged();

        if (!changed) {
          return "unchanged";
        }

        const dob = new Date(formData.dateOfBirth);
        const doi = new Date(formData.dateOfIssue);
        const doe = new Date(formData.dateOfExpire);

        if (dob > minDOB) throw new Error("Age must be at least 21.");
        if (doi > maxDOI)
          throw new Error("DL must be issued at least 1 year ago.");
        if (doe < minDOE)
          throw new Error("DL must be valid for at least 1 year.");

        setLoading(true);
        try {
          const frontUrl =
            typeof formData.dlfront === "string"
              ? formData.dlfront
              : await uploadFile(formData.dlfront, "dlfront");

          const backUrl =
            typeof formData.dlback === "string"
              ? formData.dlback
              : await uploadFile(formData.dlback, "dlback");

          const payload = {
            dlNumber: formData.dlNumber,
            nameToMatch: formData.nameToMatch,
            dateOfBirth: formData.dateOfBirth,
            dateOfIssue: formData.dateOfIssue,
            dateOfExpire: formData.dateOfExpire,
            consent: "Y",
            consentText:
              "I hereby declare my consent agreement for fetching my information via ZOOP API",
            taskId: "f26eb21e-4c35-4491-b2d5-41fa0e545a34",
            state: "",
            dlfront: frontUrl,
            dlback: backUrl,
            ownerId: driver._id,
          };

          const res = await fetch(API.ADMIN.ADMIN_VERIFY_DRIVING_LICENSE, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          });

          const result = await res.json();
          if (!result.success)
            throw new Error(result.message || "Verification failed.");

          setFormData((prev) => ({
            ...prev,
            dlfront: frontUrl,
            dlback: backUrl,
          }));

          toast.success("Driving License submitted successfully.");
          return true;
        } finally {
          setLoading(false);
        }
      },
    }));

    return (
      <div className="max-w-4xl mx-auto p-4 bg-white rounded shadow text-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block mb-1 font-medium text-sm">
              Driving License Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="dlNumber"
              value={formData.dlNumber || ""}
              onChange={handleChange}
              className="w-full border px-2 py-1 rounded text-sm"
              disabled={loading || disabled}
              required
            />
          </div>

          <div>
            <label className="block mb-1 font-medium text-sm">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="nameToMatch"
              value={formData.nameToMatch || ""}
              onChange={handleChange}
              className="w-full border px-2 py-1 rounded text-sm"
              disabled={loading || disabled}
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
          <div>
            <label className="block mb-1 font-medium text-sm">
              Date of Birth <span className="text-red-500">*</span>
            </label>
            <DatePicker
              selected={
                formData.dateOfBirth ? new Date(formData.dateOfBirth) : null
              }
              onChange={(date) =>
                setFormData((prev) => ({
                  ...prev,
                  dateOfBirth: date.toISOString().split("T")[0],
                }))
              }
              maxDate={minDOB}
              dateFormat="dd/MM/yyyy"
              showYearDropdown
              showMonthDropdown
              placeholderText="Select DOB"
              className="w-full border px-2 py-1 rounded text-sm"
              disabled={loading || disabled}
              required
            />
            <p className="text-yellow-600 text-xs mt-1">
              * Age should be minimum 21 years
            </p>
          </div>
          <div>
            <label className="block mb-1 font-medium text-sm">
              Date of Issue <span className="text-red-500">*</span>
            </label>
            <DatePicker
              selected={
                formData.dateOfIssue ? new Date(formData.dateOfIssue) : null
              }
              onChange={(date) =>
                setFormData((prev) => ({
                  ...prev,
                  dateOfIssue: date.toISOString().split("T")[0],
                }))
              }
              maxDate={maxDOI}
              dateFormat="dd/MM/yyyy"
              showYearDropdown
              showMonthDropdown
              placeholderText="Select DOI"
              className="w-full border px-2 py-1 rounded text-sm"
              disabled={loading || disabled}
              required
            />
            <p className="text-yellow-600 text-xs mt-1">
              * Driving license issue date must be at least 1 year old
            </p>
          </div>
          <div>
            <label className="block mb-1 font-medium text-sm">
              Date of Expiry <span className="text-red-500">*</span>
            </label>
            <DatePicker
              selected={
                formData.dateOfExpire ? new Date(formData.dateOfExpire) : null
              }
              onChange={(date) =>
                setFormData((prev) => ({
                  ...prev,
                  dateOfExpire: date.toISOString().split("T")[0],
                }))
              }
              minDate={minDOE}
              dateFormat="dd/MM/yyyy"
              showYearDropdown
              showMonthDropdown
              placeholderText="Select DOE"
              className="w-full border px-2 py-1 rounded text-sm"
              disabled={loading || disabled}
              required
            />
            <p className="text-yellow-600 text-xs mt-1">
              * Driving license expiry date should be minimum 1 year from today
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          <div>
            <label className="block mb-1 font-medium text-sm">
              Upload DL Front <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => handleFileChange(e, "dlfront")}
              className="w-full border px-2 py-1 rounded text-sm"
              disabled={loading || disabled}
              required={!formData.dlfront}
            />
            {dlfrontPreview && (
              <img
                src={dlfrontPreview}
                alt="DL Front"
                className="w-24 mt-1 border rounded"
              />
            )}
          </div>
          <div>
            <label className="block mb-1 font-medium text-sm">
              Upload DL Back <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(e, "dlback")}
              className="w-full border px-2 py-1 rounded text-sm"
              disabled={loading || disabled}
              required={!formData.dlback}
            />
            {dlbackPreview && (
              <img
                src={dlbackPreview}
                alt="DL Back"
                className="w-24 mt-1 border rounded"
              />
            )}
          </div>
        </div>
      </div>
    );
  }
);

export default StepDrivingLicense;
